import StudentGradebook from 'models/StudentGradebook';
import React, { useMemo } from 'react';
import PanelDropdown from 'view/components/common/PanelDropdown';
import EvaluationList from './EvaluationList';
import AssignmentList from './AssignmentList';
import {
  faBookOpen,
  faCheckCircle,
  faClipboard,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import StudentGradebookEntry from 'models/StudentGradebookEntry';
import Module from 'models/Module';
import EvaluationSubmission from 'models/EvaluationSubmission';
import { dateOrNull } from 'utils/dateUtils';

interface AssignmentDisplayProps {
  gradebook: StudentGradebook;
  previewEntry?: StudentGradebookEntry;
  previewModule?: Module;
  onAssignmentSelect: (entry: StudentGradebookEntry) => void;
  onModuleSelect: (module: Module) => void;
  onEvaluationSelect: (sub: EvaluationSubmission) => void;
}

const AssignmentDisplay: React.FC<AssignmentDisplayProps> = ({
  gradebook,
  previewEntry,
  previewModule,
  onAssignmentSelect,
  onEvaluationSelect,
  onModuleSelect,
}) => {
  const lateAssignments: StudentGradebookEntry[] = useMemo(() => {
    if (!gradebook) return [];
    return gradebook?.entries.filter((entry) => {
      const dueDate = dateOrNull(entry.assignment.due_date);
      const submittedDate = dateOrNull(entry.submission.date_completed);
      const now = new Date();

      return (
        entry.visible &&
        entry.assignment.module === null &&
        dueDate &&
        !submittedDate &&
        dueDate < now
      );
    });
  }, [gradebook]);

  const todoAssignments: StudentGradebookEntry[] = useMemo(() => {
    if (!gradebook) return [];
    return gradebook?.entries.filter((entry) => {
      const dueDate = dateOrNull(entry.assignment.due_date);
      const submittedDate = dateOrNull(entry.submission.date_completed);
      const now = new Date();
      return (
        entry.visible &&
        entry.assignment.module === null &&
        ((!dueDate && !submittedDate) ||
          (dueDate && !submittedDate && dueDate > now))
      );
    });
  }, [gradebook]);

  const doneAssignments: StudentGradebookEntry[] = useMemo(() => {
    if (!gradebook) return [];
    return gradebook?.entries.filter((entry) => {
      const submittedDate = dateOrNull(entry.submission.date_completed);
      return (
        entry.visible && entry.assignment.module === null && !!submittedDate
      );
    });
  }, [gradebook]);

  const evaluations: EvaluationSubmission[] = useMemo(() => {
    if (!gradebook) return [];
    return gradebook?.evaluations.filter(
      (evaluation) => !evaluation.date_completed,
    );
  }, [gradebook]);

  return (
    <div className="item-list">
      <PanelDropdown
        icon={faClipboard}
        label={`Evaluations (${evaluations.length})`}
      >
        <EvaluationList
          evaluations={evaluations}
          selected={undefined}
          onSelect={onEvaluationSelect}
        />
      </PanelDropdown>
      <PanelDropdown
        icon={faExclamationCircle}
        label={`Overdue (${lateAssignments.length})`}
        startExpanded={true}
      >
        <AssignmentList
          type="late"
          assignments={lateAssignments}
          selected={previewEntry}
          selectedModule={previewModule}
          onSelect={onAssignmentSelect}
          onSelectModule={onModuleSelect}
        />
      </PanelDropdown>
      <PanelDropdown
        icon={faBookOpen}
        label={`To do (${todoAssignments.length})`}
        startExpanded={true}
      >
        <AssignmentList
          type="todo"
          assignments={todoAssignments}
          modules={gradebook?.modules}
          selected={previewEntry}
          selectedModule={previewModule}
          onSelect={onAssignmentSelect}
          onSelectModule={onModuleSelect}
        />
      </PanelDropdown>
      <PanelDropdown
        icon={faCheckCircle}
        label={`Done (${doneAssignments.length})`}
      >
        <AssignmentList
          type="done"
          assignments={doneAssignments}
          selected={previewEntry}
          selectedModule={previewModule}
          onSelect={onAssignmentSelect}
          onSelectModule={onModuleSelect}
        />
      </PanelDropdown>
    </div>
  );
};

export default AssignmentDisplay;
